// Copilot API types and client
import apiClient from "../../services/apiClient";
import { UserAppState } from "../../hooks/useUserAppState";
import { ConversationMetadata } from "./utils";

export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
  createdAt?: Date;
}

export interface MessageHistoryDto {
  role: string;
  content: string;
}

export interface ChatRequest {
  message: string;
  conversationId?: number;
  sessionId?: string;
  mode?: string; // Prompt mode: "onboarding", "help-general", etc.
  customPrompt?: string; // Complete system prompt override for Lab testing (includes prompt + knowledge + context)
  testAccountId?: number; // Lab: Test with different account context (read-only)
  history?: MessageHistoryDto[]; // Lab: Frontend-cached message history for test mode
  bypassRateLimits?: boolean; // Lab: Bypass rate limits for testing (SuperUser only)
  pageContext?: PageContextDto; // Current page context for contextual help (deprecated - use userAppState)
  userAppState?: UserAppState; // Current user app state for semantic actions (includes route, feature, IDs, page flags)
  timezoneOffset?: string; // User's timezone offset from UTC (e.g., "-05:00")
  timezoneName?: string; // User's IANA timezone name (e.g., "America/New_York")
}

export interface PageContextDto {
  route: string;
  catalogId?: number;
  productId?: number;
  feature?: string;
}

export interface ChatResponse {
  conversationId: number | null; // Null in test mode
  response: string;
  toolCalls?: ToolCallInfo[];
  articlesUsed?: ArticleSummary[];
  inputTokens: number;
  outputTokens: number;
  cost: number;
  cacheCreationInputTokens: number;
  cacheReadInputTokens: number;
  rateLimitInfo?: RateLimitInfo;
}

export interface RateLimitInfo {
  inputTokensRemaining: number;
  inputTokensLimit: number;
  outputTokensRemaining: number;
  outputTokensLimit: number;
  requestsRemaining: number;
  requestsLimit: number;
  resetTime?: string;
}

export interface ToolCallInfo {
  toolName: string;
  parameters: string;
  result?: string;
  executionTimeMs: number;
}

export interface ArticleSummary {
  id: number;
  title: string;
  summary?: string;
  category?: string;
}

export interface FeedbackRequest {
  conversationId: number;
  messageId?: number;
  rating: number;
  comment?: string;
  resolutionType?: string;
}

export interface ConversationSummary {
  id: number;
  userId: number;
  status: string;
  resolutionStatus?: string;
  startedAt: Date;
  lastActivityAt: Date;
  resolvedAt?: Date;
  totalMessages: number;
  totalToolCalls: number;
  totalCost: number;
  title?: string;
  summary?: string;

  // Prompt token breakdown
  promptTokens?: number; // Base prompt (cacheable)
  knowledgeTokens?: number; // System knowledge (cacheable)
  appMapTokens?: number; // App map (cacheable 24h)
  modeTokens?: number; // Mode prompt (variable)
  contextTokens?: number; // User context (1h cache)
  appStateTokens?: number; // User app state (real-time)
  isCustomPrompt?: boolean; // Lab override flag
  mode?: string; // Conversation mode: "onboarding", "help-general", etc.
}

// Admin-specific conversation with enriched data
export interface AdminConversationSummary extends ConversationSummary {
  accountId: number;
  accountNamespace: string;
  businessName?: string;
  userEmail: string;
  userName?: string;
  totalTokens: number; // Sum of all inputTokens + outputTokens
  topics?: string[]; // Topics extracted from metadata
  metadata?: string; // Raw metadata JSON
  parsedMetadata?: ConversationMetadata; // Parsed metadata (cached)
  adminStatus?: string; // ok, review, content, tuning, bug
  adminNotes?: string; // Free-form admin notes
  isAccountDeleted?: boolean; // True if account was deleted (data from snapshot)
  // Account enrichment
  planCode?: string;
  monthlyPrice?: number;
  gateway?: string;
  accountCreated?: string;
  sessions?: number;
  country?: string;
  /** A/B/C — derived from country + gateway, drives the grant and the AI config arms */
  countryTier?: string;
}

// Full conversation details with all related data
export interface ConversationFullDetails {
  id: number;
  userId: number;
  accountId: number;
  accountNamespace: string;
  businessName?: string;
  userEmail: string;
  userName?: string;
  planCode?: string;
  monthlyPrice?: number;
  gateway?: string;
  accountCreated?: string;
  sessions?: number;
  country?: string;
  countryTier?: string; // A/B/C — derived from country + gateway
  isAccountDeleted?: boolean; // True if account was deleted (data from snapshot)
  title?: string;
  summary?: string;
  status: string;
  resolutionStatus?: string;
  userFeedback?: string;
  startedAt: Date;
  lastActivityAt: Date;
  resolvedAt?: Date;
  totalMessages: number;
  totalToolCalls: number;
  totalCost: number;
  totalTokens: number;
  promptTokens?: number;
  knowledgeTokens?: number;
  appMapTokens?: number;
  modeTokens?: number;
  contextTokens?: number;
  appStateTokens?: number;
  isCustomPrompt?: boolean;
  metadata?: string; // Raw metadata JSON
  parsedMetadata?: ConversationMetadata; // Parsed metadata (cached)
  adminStatus?: string; // ok, review, content, tuning, bug
  adminNotes?: string; // Free-form admin notes
  mode?: string; // Conversation mode: "onboarding", "help-general", etc.
  /** Claude model that handled the conversation (from metadata); null for pre-recording conversations */
  model?: string;
  /** Admin email if this conversation was run via "login as user" impersonation; null for real users */
  impersonatedBy?: string;
  messages: MessageFullDetails[];
}

// Message with cache information
export interface MessageFullDetails extends MessageDetails {
  cacheCreationInputTokens: number;
  cacheReadInputTokens: number;
}

// Summary statistics for filtered conversations
export interface ConversationStats {
  totalConversations: number;
  totalCost: number;
  avgCost: number;
  totalTokens: number;
  resolvedCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  resolvedRate: number;
}

// Admin conversations response
export interface AdminConversationsResponse {
  conversations: AdminConversationSummary[];
  totalCount: number;
  stats: ConversationStats;
}

// Filter options
export interface ConversationFilterOptions {
  skip?: number;
  take?: number;
  sortBy?: string;
  sortDir?: string;
  dateFrom?: string;
  dateTo?: string;
  accountId?: number;
  userId?: number;
  status?: string;
  resolutionStatus?: string;
  adminStatus?: string; // ok, review, content, tuning, bug, none (empty)
  query?: string; // Search by email, namespace, businessName
  topics?: string[]; // Client-side filter for topics
  excludeTest?: boolean; // Exclude @catalogmachine.com users
  deletedOnly?: boolean; // Show only deleted accounts (client-side filter)
  countryTier?: string; // A, B, C — country tier (derived from country + gateway)
}

export interface TestAccountInfo {
  accountId: number;
  namespace: string;
  businessName: string;
  adminEmail: string;
}

export interface UserDto {
  id: number;
  name: string;
  email: string;
  role: string; // "Admin" or "User"
  created: Date;
}

export interface CopilotUserContext {
  journey: JourneySummary;
  activity: ActivitySummary;
  profile: ProfileDto;
  usage: UsageContext;
  user?: UserDto;
  accountId: number;
  namespace: string;
  businessName: string;
  planCode: string;
  planName: string;
  monthlyPrice: number;
  created: Date;
  country?: string;
  accountAge?: string;
}

export interface JourneySummary {
  totalMilestones: number;
  daysSinceLastMilestone?: number;
  phase: string;
  onboarding: JourneyCategory;
  products: JourneyCategory;
  catalogs: JourneyCategory;
  integrations: JourneyCategory;
}

export interface JourneyCategory {
  completedCount: number;
  lastMilestone?: string;
  daysSinceLastMilestone?: number;
  status: string;
}

export interface ActivitySummary {
  lastSignificantAction?: string;
  sessions24h: number;
  featureEvents24h: number;
  significantEvents7d: number;
  recentErrors: string[];
}

export interface ProfileDto {
  industry?: string;
  role?: string;
  businessName?: string;
  catalogType?: string;
  planMax?: string;
  signupSource?: string;
  urlReferrer?: string;
}

export interface UsageContext {
  sessions: number;
  products: UsageItem;
  catalogs: UsageItem;
  storageMb: UsageItem;
  users: UsageItem;
  showrooms: UsageItem;
  maxVariants: number;
  maxCatalogPageLimit: number;
  categoriesCount: number;
  imagesCount: number;
  productCardsCount: number;
  activeUsersCount: number;
  publishedCatalogs: number;
}

export interface UsageItem {
  count: number;
  limit?: number;
}

export interface MessageDetails {
  id: number;
  role: string;
  content: string;
  createdAt: string;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  toolCalls?: ToolCallInfo[];
}

export interface ConversationDetails {
  id: number;
  userId: number;
  accountId: number;
  status: string;
  resolutionStatus?: string;
  startedAt: string;
  resolvedAt?: string;
  totalMessages: number;
  totalToolCalls: number;
  totalCost: number;
  messages: MessageDetails[];
}

export interface PromptPreviewResponse {
  prompt: string;
  accountId: number;
  mode?: string;
  tokenCount: number;
  promptTokens: number; // Foundation + AI Instructions
  knowledgeTokens: number; // System knowledge articles
  appMapTokens: number; // App map (cacheable 24h)
  modeTokens: number; // Mode-specific prompt
  userContextTokens: number; // User journey + activity + usage (1h cache)
  appStateTokens: number; // User app state (real-time)
}

class CopilotApiClient {
  private baseUrl = "/copilot";

  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    const response = await apiClient.post<ChatResponse>(
      `${this.baseUrl}/chat`,
      request,
      {
        timeout: 300000,
      },
    );
    return response.data;
  }

  async saveFeedback(feedback: FeedbackRequest): Promise<void> {
    await apiClient.post(`${this.baseUrl}/feedback`, feedback);
  }

  async getConversations(
    skip = 0,
    take = 50,
    testAccountId?: number,
  ): Promise<ConversationSummary[]> {
    const response = await apiClient.get<ConversationSummary[]>(
      `${this.baseUrl}/conversations`,
      { params: { skip, take, testAccountId } },
    );
    return response.data;
  }

  async getTestAccountInfo(accountId: number): Promise<TestAccountInfo> {
    const response = await apiClient.get<TestAccountInfo>(
      `${this.baseUrl}/test-account/${accountId}`,
    );
    return response.data;
  }

  async getTestUserContext(accountId?: number): Promise<CopilotUserContext> {
    const response = await apiClient.get<CopilotUserContext>(
      `${this.baseUrl}/test-context/${accountId || 0}`,
    );
    return response.data;
  }

  async getConversation(id: number): Promise<ChatMessage[]> {
    const response = await apiClient.get<ConversationDetails>(
      `${this.baseUrl}/conversation/${id}`,
    );
    // Convert MessageDetails to ChatMessage format
    const messages: ChatMessage[] = response.data.messages.map((msg) => ({
      role: msg.role as "user" | "assistant" | "system",
      content: msg.content,
      createdAt: new Date(msg.createdAt),
    }));

    return messages;
  }

  async getPromptPreview(
    testAccountId?: number,
    mode?: string,
  ): Promise<PromptPreviewResponse> {
    const response = await apiClient.get<PromptPreviewResponse>(
      `${this.baseUrl}/preview-prompt`,
      { params: { testAccountId, mode } },
    );
    return response.data;
  }

  async getAdminConversations(
    filters: ConversationFilterOptions,
  ): Promise<AdminConversationsResponse> {
    const response = await apiClient.get<AdminConversationsResponse>(
      `${this.baseUrl}/admin/conversations`,
      { params: filters },
    );
    return response.data;
  }

  async getConversationFullDetails(
    conversationId: number,
  ): Promise<ConversationFullDetails> {
    const response = await apiClient.get<ConversationFullDetails>(
      `${this.baseUrl}/admin/conversation/${conversationId}/full`,
    );
    return response.data;
  }

  async updateConversationAdminStatus(
    conversationId: number,
    status: string | null,
  ): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(
      `${this.baseUrl}/admin/conversation/${conversationId}/status`,
      { status },
    );
    return response.data;
  }

  async updateConversationAdminNotes(
    conversationId: number,
    notes: string | null,
  ): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(
      `${this.baseUrl}/admin/conversation/${conversationId}/notes`,
      { notes },
    );
    return response.data;
  }

  async exportConversation(conversationId: number): Promise<string> {
    const response = await apiClient.get<string>(
      `${this.baseUrl}/admin/conversation/${conversationId}/export`,
      { responseType: "text" as never },
    );
    return response.data;
  }

  /**
   * Export filtered conversations as .md file download.
   * Filename includes date range and active filter tags (same pattern as account exports).
   */
  async exportConversations(filters: ConversationFilterOptions): Promise<void> {
    const response = await apiClient.get(
      `${this.baseUrl}/admin/conversations/export`,
      { params: filters, responseType: "blob" },
    );

    // Build descriptive filename from active filters
    const parts: string[] = [];
    if (filters.status) parts.push(filters.status);
    if (filters.resolutionStatus) parts.push(filters.resolutionStatus);
    if (filters.adminStatus) parts.push(filters.adminStatus);
    if (filters.query)
      parts.push(filters.query.substring(0, 20).replace(/\s+/g, "-"));
    if (filters.excludeTest) parts.push("real");
    if (filters.deletedOnly) parts.push("deleted");
    if (filters.countryTier) parts.push(`tier${filters.countryTier}`);
    if (filters.topics?.length) parts.push(`t${filters.topics.length}`);

    // Date range tag: mar10-mar17
    if (filters.dateFrom || filters.dateTo) {
      const fmt = (d: string) => {
        const dt = new Date(d);
        return dt
          .toLocaleDateString("en-US", { month: "short", day: "numeric" })
          .toLowerCase()
          .replace(" ", "");
      };
      if (filters.dateFrom && filters.dateTo)
        parts.push(`${fmt(filters.dateFrom)}-${fmt(filters.dateTo)}`);
      else if (filters.dateFrom) parts.push(`from-${fmt(filters.dateFrom)}`);
      else if (filters.dateTo) parts.push(`to-${fmt(filters.dateTo)}`);
    }

    const suffix = parts.length > 0 ? `-${parts.join("-")}` : "";
    const fileName = `copilot-conversations-${new Date().toISOString().split("T")[0]}${suffix}.md`;

    const blob = new Blob([response.data], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async bulkUpdateAdminStatus(
    conversationIds: number[],
    status: string | null,
  ): Promise<{ success: boolean; updated: number }> {
    const response = await apiClient.post<{
      success: boolean;
      updated: number;
    }>(`${this.baseUrl}/admin/conversations/bulk-status`, {
      conversationIds,
      status,
    });
    return response.data;
  }
}

export const copilotApi = new CopilotApiClient();
